import jwtDecode from 'jwt-decode';
import axios from 'src/utils/axios';
// import axios from 'axios';

// var api = axios.create({
//   baseURL: "http://localhost:3000"
// })

// CON-1966. While signed in as someone, the original account's token and
// the label for its Return button are kept here, beside 'accessToken'. Both
// live in localStorage on purpose: they must last exactly as long as the
// consultant's token does (which is in localStorage), so a reload or a
// closed-and-reopened tab still shows the "Viewing as" pill and can still
// return.
// sessionStorage would lose the original on tab close while the consultant
// session carried on, unmarked. Nothing ever sends the original token
// except the Return itself; every request uses 'accessToken'.
const SIGN_IN_AS_ORIGINAL_TOKEN_KEY = 'signInAsOriginalAccessToken';
const SIGN_IN_AS_ORIGINAL_LABEL_KEY = 'signInAsOriginalLabel';

class AuthService {
  setAxiosInterceptors = ({ onLogout }) => {
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && (error.response.status === 401 || (error.response.status === 500 && error.response.data === 'Not authorized'))) {
          this.setSession(null);

          if (onLogout) {
            onLogout();
          }
        }

        return Promise.reject(error);
      }
    );
  };

  handleAuthentication() {
    const accessToken = this.getAccessToken();

    if (!accessToken) {
      return;
    }

    if (this.isValidToken(accessToken)) {
      this.setSession(accessToken);
    } else {
      this.setSession(null);
    }
  }

  loginWithEmailAndPassword = (email, password, createAccount) => new Promise((resolve, reject) => {
    axios
      .post('/api/account/login', { email, password, createAccount })
      .then((response) => {
        if (response.data.user) {
          this.setSession(response.data.accessToken);
          resolve(response.data);
        } else {
          reject(response.data.error);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });

  loginInWithToken = () => new Promise((resolve, reject) => {
    axios
      .get('/api/account/me')
      .then((response) => {
        if (response.data.user) {
          resolve(response.data);
        } else {
          reject(response.data.error);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });

  logout = () => {
    this.setSession(null);
  };

  setSession = (accessToken) => {
    if (accessToken) {
      localStorage.setItem('accessToken', accessToken);
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    } else {
      localStorage.removeItem('accessToken');
      delete axios.defaults.headers.common.Authorization;
      // CON-1966. Logging out, or losing the session, ends a sign-in-as
      // too: never leave an original token behind.
      this.clearSignInAsOriginal();
    }
  };

  // CON-1966. The saved original account, or null when not signed in as
  // anyone: { token, label }.
  getSignInAsOriginal = () => {
    const token = localStorage.getItem(SIGN_IN_AS_ORIGINAL_TOKEN_KEY);
    if (!token) return null;
    return {
      token,
      label: localStorage.getItem(SIGN_IN_AS_ORIGINAL_LABEL_KEY) || 'Support',
    };
  };

  clearSignInAsOriginal = () => {
    localStorage.removeItem(SIGN_IN_AS_ORIGINAL_TOKEN_KEY);
    localStorage.removeItem(SIGN_IN_AS_ORIGINAL_LABEL_KEY);
  };

  // CON-1966. Switch to the consultant's token. The first switch saves the
  // current (original) token; a later one keeps that original, so a second
  // sign-in-as never chains.
  startSignInAs = (consultantToken, originalLabel) => {
    if (!this.getSignInAsOriginal()) {
      const currentToken = this.getAccessToken();
      if (currentToken) {
        localStorage.setItem(SIGN_IN_AS_ORIGINAL_TOKEN_KEY, currentToken);
        localStorage.setItem(SIGN_IN_AS_ORIGINAL_LABEL_KEY, originalLabel || 'Support');
      }
    }
    this.setSession(consultantToken);
  };

  // CON-1966. Back to the original account. Returns false, with both
  // sessions cleared, when the original is missing or has expired: the
  // caller then goes to the login page.
  returnFromSignInAs = () => {
    const original = this.getSignInAsOriginal();
    if (!original || !this.isValidToken(original.token)) {
      this.setSession(null);
      return false;
    }
    this.clearSignInAsOriginal();
    this.setSession(original.token);
    return true;
  };

  getAccessToken = () => localStorage.getItem('accessToken');

  isValidToken = (accessToken) => {
    if (!accessToken) {
      return false;
    }

    try {
      const decoded = jwtDecode(accessToken);
      const currentTime = Date.now() / 1000;

      return decoded.exp > currentTime;
    } catch (error) {
      return false;
    }
  };

  isAuthenticated = () => !!this.getAccessToken();
}

const authService = new AuthService();

export default authService;
